import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import api from "api/api";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { ApiResponse } from "model/apiResponseModel";

const fieldSx = {
  "& .MuiInputBase-root": {
    display: "flex !important",
    placeItems: "unset !important",
    alignItems: "center !important",
    justifyContent: "flex-start !important",
    width: "100% !important",
  },
  "& .MuiInputBase-input": {
    width: "100% !important",
    textAlign: "left !important",
  },
};

const selectSx = {
  "& .MuiInputBase-root": {
    display: "flex !important",
    placeItems: "unset !important",
    alignItems: "center !important",
    justifyContent: "flex-start !important",
    width: "100% !important",
  },
  "& .MuiSelect-select": {
    textAlign: "left !important",
    width: "100% !important",
  },
};

const dateInputSx = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  borderRadius: "8px",
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  fontSize: "0.875rem",
  color: "#344767",
  backgroundColor: "#fff",
  boxSizing: "border-box",
  "&:focus": {
    outline: "none",
    borderColor: "#17c1e8",
    boxShadow: "0 0 0 1px #17c1e8",
  },
};

const iconButtonSx = {
  backgroundColor: "info.main",
  color: "#ffffff",
  borderRadius: "50%",
  width: "32px",
  height: "32px",
  flexShrink: 0,
  "&:hover": {
    backgroundColor: "info.main",
    opacity: 0.9,
  },
};

const removeIconButtonSx = {
  ...iconButtonSx,
  backgroundColor: "error.main",
  "&:hover": {
    backgroundColor: "error.main",
    opacity: 0.9,
  },
};

const toExpiryISOString = (datetimeLocal) => {
  if (!datetimeLocal) return "";
  const date = new Date(datetimeLocal);
  return date.toISOString();
};

export default function IssueRewardDialog({ open, onClose, onSubmit, loading }) {
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [form, setForm] = useState({
    rewardId: "",
    benefits: [""],
    expiry_at: "",
  });

  const fetchRewards = async () => {
    try {
      setRewardsLoading(true);
      const response = await api.getRewards();
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        const rewardsList = (response.data?.rewards || response.data || []).filter(
          (reward) => reward.is_enabled && !reward.is_deleted
        );
        setRewards(rewardsList);
      } else {
        setRewards([]);
      }
    } catch (error) {
      console.error("Error fetching rewards:", error);
      setRewards([]);
    } finally {
      setRewardsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm({
        rewardId: "",
        benefits: [""],
        expiry_at: "",
      });
      fetchRewards();
    }
  }, [open]);

  const handleBenefitChange = (index, value) => {
    setForm((prev) => {
      const updatedBenefits = [...prev.benefits];
      updatedBenefits[index] = value;
      return { ...prev, benefits: updatedBenefits };
    });
  };

  const handleAddBenefit = () => {
    setForm((prev) => ({
      ...prev,
      benefits: [...prev.benefits, ""],
    }));
  };

  const handleRemoveBenefit = (index) => {
    setForm((prev) => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    const filteredBenefits = form.benefits.map((b) => b.trim()).filter(Boolean);

    const result = await onSubmit({
      rewardId: form.rewardId,
      benefits: filteredBenefits,
      expiry_at: toExpiryISOString(form.expiry_at),
    });

    if (result?.success) {
      onClose();
    }
  };

  const filteredBenefits = form.benefits.map((b) => b.trim()).filter(Boolean);
  const isFormValid =
    form.rewardId && filteredBenefits.length > 0 && form.expiry_at;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle>
        <SoftTypography variant="h5" fontWeight="bold">
          Issue Reward
        </SoftTypography>
        <SoftTypography variant="button" color="text" fontWeight="regular">
          Assign a reward to this user with custom benefits and expiry.
        </SoftTypography>
      </DialogTitle>

      <DialogContent>
        <Box mt={1} display="flex" flexDirection="column" gap={3}>
          {/* Reward Select */}
          <Box display="flex" flexDirection="column" gap={1}>
            <SoftTypography variant="button" fontWeight="medium">
              Reward
            </SoftTypography>
            <FormControl fullWidth sx={{ ...selectSx, backgroundColor: "white" }}>
              <Select
                name="rewardId"
                value={form.rewardId}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, rewardId: e.target.value }))
                }
                displayEmpty
                disabled={rewardsLoading}
                sx={{ minHeight: "45px" }}
              >
                <MenuItem value="" disabled>
                  {rewardsLoading ? "Loading rewards..." : "Choose a reward..."}
                </MenuItem>
                {rewards.map((reward) => (
                  <MenuItem key={reward._id} value={reward._id}>
                    {reward.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Benefits */}
          <Box display="flex" flexDirection="column" gap={1}>
            <SoftTypography variant="button" fontWeight="medium">
              Benefits
            </SoftTypography>
            <Box
              sx={{
                backgroundColor: "#fcfaff",
                p: 2,
                borderRadius: "12px",
                border: "1px dashed #d1c4e9",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {form.benefits.map((benefit, index) => (
                <Box key={index} display="flex" alignItems="center" gap={1}>
                  {form.benefits.length > 1 && (
                    <IconButton
                      onClick={() => handleRemoveBenefit(index)}
                      sx={removeIconButtonSx}
                      size="small"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  )}
                  <TextField
                    fullWidth
                    placeholder="Enter benefit"
                    value={benefit}
                    onChange={(e) => handleBenefitChange(index, e.target.value)}
                    sx={fieldSx}
                  />
                  {benefit.trim() && index === form.benefits.length - 1 && (
                    <IconButton
                      onClick={handleAddBenefit}
                      sx={iconButtonSx}
                      size="small"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Expiry Date */}
          <Box display="flex" flexDirection="column" gap={1}>
            <SoftTypography variant="button" fontWeight="medium">
              Expiry Date & Time
            </SoftTypography>
            <Box
              component="input"
              type="datetime-local"
              value={form.expiry_at}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, expiry_at: e.target.value }))
              }
              sx={dateInputSx}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <SoftButton onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </SoftButton>
        <SoftButton
          onClick={handleSubmit}
          variant="gradient"
          color="info"
          disabled={loading || !isFormValid}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Issue Reward"
          )}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

IssueRewardDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};
